import { PhotoGallery } from "@/modes/walk-mode/photo-preview-list";
import {
  isElementPhotosGroup,
  PhotosGroup,
  TimestampElement,
} from "@/modes/walk-mode/walk-overlay-selectors";
import {
  selectIElementsSlice,
  type OnlyIElementsStore,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  FaroText,
  grey,
  NoTranslate,
  ScanIcon,
  TruncatedFaroTextWithSuffix,
} from "@faro-lotv/flat-ui";
import {
  convertToDateString,
  convertToDateTimeString,
  convertToTimeString,
} from "@faro-lotv/foundation";
import {
  GUID,
  IElement,
  IElementImg2d,
  IElementType,
  IElementTypeHint,
  isIElementGenericDataSession,
  isIElementGenericDataset,
  isIElementImg2d,
  isIElementImg360,
  isIElementOdometryPathSection,
  isIElementSection,
  isIElementTimeSeriesRoom,
  isIElementVideoRecording,
  pickClosestInTime,
} from "@faro-lotv/ielement-types";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import { Stack } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useMemo, type JSX } from "react";
import { TimestampDropdown } from "./timestamp-dropdown/timestamp-dropdown";
import { ContextMenu } from "./tree/tree-context-menu";
import { BaseContextMenuAction } from "./tree/tree-context-menu/action-types";
import { useScanDate } from "./use-scan-date";

type DataSessionsDropDownProps = {
  /** The list of datasets of interest for the current mode */
  datasets: TimestampElement[];

  /** The photo gallery for the datasets */
  photoGallery?: PhotoGallery;

  /** The element containing the time reference for the current active element  */
  referenceElement?: IElement;

  /**
   * The element that is currently active in the scene.
   * This will be used to determine the label to show next to the dropdown.
   * If not provided, the label will be derived from the `referenceElement`.
   */
  activeElement?: IElement;

  /** Called when the user selects a new data session from the drop down */
  onDataSessionChanged(id: GUID): void;

  /** Callback executed when an element that is in use get deleted */
  handleElementDeletion?: BaseContextMenuAction["handleElementDeletion"];
};

/**
 * @returns a dropdown to select the available odometric paths
 */
export function DataSessionsDropDown({
  datasets,
  activeElement,
  referenceElement,
  onDataSessionChanged,
  photoGallery,
  handleElementDeletion,
}: DataSessionsDropDownProps): JSX.Element | null {
  const store = useAppStore();

  const selectedElement = useAppSelector(
    selectSelectedElement(activeElement, referenceElement, datasets),
  );

  /** Active element or selected element if available */
  const mainElement = activeElement ?? selectedElement;

  /** Parent element of the main element */
  const parentElement = useAppSelector(
    selectIElement(
      selectedElement && isElementPhotosGroup(selectedElement)
        ? undefined
        : selectedElement?.parentId,
    ),
  );

  /** Parent section of the main element */
  const parentSection = useAppSelector(
    selectAncestor(
      mainElement && isElementPhotosGroup(mainElement)
        ? undefined
        : mainElement,
      isIElementSection,
    ),
  );

  const parentTimeseriesRoom = useAppSelector(
    selectAncestor(
      mainElement && isElementPhotosGroup(mainElement)
        ? undefined
        : mainElement,
      isIElementTimeSeriesRoom,
    ),
  );

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const isRoomPano = !!parentTimeseriesRoom;

  /** True if the active element is an odometry path. */
  const isPath =
    !!parentSection && isIElementOdometryPathSection(parentSection);

  /** True if the user is currently viewing an image (2D or 360) */
  const isViewingImage =
    mainElement &&
    (isElementPhotosGroup(mainElement) ||
      isIElementImg2d(mainElement) ||
      isIElementImg360(mainElement));

  /** The scan date, if available, to show next to the dropdown. */
  const scanDate = useScanDate(mainElement, parentSection);

  // Show the scan date next to the dropdown when viewing a pano and
  // if the active element is not a room pano or a pano from an odometry path
  const showScanDate = !!scanDate && !isRoomPano && !isPath;

  /** Show the label for the selected element when viewing a pano */
  const showScanLabel = isViewingImage;

  /**
   * The label to display next to the dropdown.
   * If the active element is a pano, displays the name of the section it belongs to.
   * If the active element is a pano from an odometry path, displays the name of the pano instead.
   * If the active element is a photo group or a 2D photo, displays the time the photo was taken if photo grouping is enabled.
   */
  const label = useMemo(() => {
    if (
      !mainElement ||
      (!isElementPhotosGroup(mainElement) &&
        !isIElementImg360(mainElement) &&
        !isIElementImg2d(mainElement))
    ) {
      return;
    }

    if (isElementPhotosGroup(mainElement) || isIElementImg2d(mainElement)) {
      return convertToTimeString(mainElement.createdAt);
    }

    if (isElementPhotosGroup(mainElement)) return;

    // If the active element is a pano, we want to show the name of the section it belongs to
    if (isPath) {
      // If the parent section is an odometry path, we want to show the name of the pano instead.
      // That's mainly because the name of the odometry path is usually "ImgSnake", which is not very informative.
      return mainElement.name;
    }

    return parentSection?.name;
  }, [isPath, mainElement, parentSection?.name]);

  // Name of the selected element
  const labelText = useMemo(
    () =>
      label ??
      (selectedElement && isElementPhotosGroup(selectedElement)
        ? ""
        : selectedElement?.name) ??
      "",
    [label, selectedElement],
  );

  const labelFormatter = useCallback(
    (iElement: TimestampElement) => {
      // If photo grouping is enabled and the element is a photo group, show the date and the number of photos in the group.
      if (isElementPhotosGroup(iElement)) {
        return (
          <NoTranslate>{convertToDateString(iElement.createdAt)}</NoTranslate>
        );
      }

      const optionParentTimeSeriesRoom = selectAncestor(
        isElementPhotosGroup(iElement) ? undefined : iElement,
        isIElementTimeSeriesRoom,
      )(store.getState());

      const showElementName = !optionParentTimeSeriesRoom;
      const showElementTime = optionParentTimeSeriesRoom;

      // Flag to show or not the date of the element.
      // It's hidden for datasets.
      const shownDate =
        !isIElementGenericDataset(iElement) &&
        !isIElementGenericDataSession(iElement);

      const videoRecording = selectAncestor(
        iElement,
        isIElementVideoRecording,
      )(store.getState());

      const referenceElement = videoRecording ?? iElement;

      // Show the name and date of the IElement of the dropdown option
      return (
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
          {showElementName && (
            <TruncatedFaroTextWithSuffix
              text={iElement.name}
              lengthOfSuffix={3}
              variant="bodyM"
              containerSx={{
                flexGrow: 1,
                minWidth: 0,
              }}
              sx={{
                textTransform: "none",
                textWrap: "nowrap",
                color: "currentcolor",
              }}
            />
          )}
          {shownDate && (
            <NoTranslate>
              {showElementTime
                ? convertToDateTimeString(referenceElement.createdAt)
                : convertToDateString(referenceElement.createdAt)}
            </NoTranslate>
          )}
        </Stack>
      );
    },
    [store],
  );

  // Check if datasets contains referenceElement since it could be a room and we don't
  // want to show it
  if (datasets.length === 0 || !selectedElement) {
    return null;
  }

  // If the active element is a pano from a room, show only its name and timestamp.
  // Otherwise, show both the name of the dataset the pano is from and the name of the pano itself.
  return (
    <Stack
      direction="row"
      aria-label="data sessions dropdown"
      sx={{
        backgroundColor: grey[1000],
        zIndex: 0,
        alignItems: "center",
        paddingY: 0.625,
        paddingX: 1.5,
        gap: 0.75,
        borderRadius: "4px",
        width: "100%",
      }}
    >
      <TimestampDropdown
        value={selectedElement.id}
        options={datasets}
        photoGallery={photoGallery}
        onChange={(e) => onDataSessionChanged(e.target.value)}
        labelFormatter={labelFormatter}
        containerSx={{
          minWidth: 0,
          // Can shrink but with lower priority
          flexShrink: 1,
        }}
        sx={{ color: grey[100] }}
      />

      {showScanDate && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            // Can shrink but with lower priority
            flexShrink: 1,
            overflow: "hidden",
          }}
        >
          <ScanIcon sx={{ fontSize: "18px", color: grey[100] }} />
          <FaroText
            variant="bodyM"
            sx={{ textWrap: "nowrap", color: grey[100] }}
          >
            {convertToDateTimeString(scanDate)}
          </FaroText>
        </Stack>
      )}

      {showScanLabel && (
        <TruncatedFaroTextWithSuffix
          text={labelText}
          lengthOfSuffix={3}
          variant="bodyM"
          containerSx={{
            flexGrow: 1,
            minWidth: 0,
            // Higher shrink priority - shrinks first and more aggressively
            flexShrink: 2,
          }}
          sx={{
            textWrap: "nowrap",
            color: grey[100],
          }}
        />
      )}

      {((parentElement?.type === IElementType.timeSeries &&
        parentElement.typeHint === IElementTypeHint.room) ||
        isElementPhotosGroup(selectedElement)) && (
        <ContextMenu
          dark
          id={
            isElementPhotosGroup(selectedElement) && activeElement
              ? activeElement.id
              : selectedElement.id
          }
          isNodeDisabled={hasWritePermission}
          handleElementDeletion={handleElementDeletion}
        />
      )}
    </Stack>
  );
}

/**
 * Find the photo group from timestamp element list for a given 2D photo element
 *
 * @param elements the timestamp element list
 * @param photo the given 2D photo ielement
 * @returns the photo group if found; undefined if not found
 */
export function findSelectedPhotosGroup(
  elements: TimestampElement[],
  photo: IElementImg2d,
): PhotosGroup | undefined {
  const photoGroup = elements
    .filter((el) => isElementPhotosGroup(el))
    .find((group) => group.photos.includes(photo));

  return photoGroup;
}

/**
 * The selected element should be the ancestor dataset (if it exists) or the
 * one closest in time
 *
 * @param state The app state
 * @param activeElement The current active element
 * @param referenceElement The current section IElement used for time reference
 * @param datasets The list of available datasets in the current scene
 * @returns The selected iElement given the current area scene
 */
const selectSelectedElement = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, activeElement: IElement | undefined) =>
        activeElement,
      (
        state: OnlyIElementsStore,
        activeElement: IElement | undefined,
        referenceElement: IElement | undefined,
      ) => referenceElement,
      (
        state: OnlyIElementsStore,
        activeElement: IElement | undefined,
        referenceElement: IElement | undefined,
        datasets: TimestampElement[],
      ) => datasets,
    ],
    (iElements, activeElement, referenceElement, datasets) => {
      if (activeElement && isIElementImg2d(activeElement)) {
        return findSelectedPhotosGroup(datasets, activeElement);
      }

      if (referenceElement) {
        const elements = datasets.map((el) =>
          isElementPhotosGroup(el) ? el.photos[0] : el,
        );

        // The reference element may be a single scan in the dataset.
        // If that's the case, the selected element should be the dataset
        // itself, that may have a date different from the reference element
        const datasetMap = new Set(elements.map((e) => e.id));
        const ancestor = selectAncestor(referenceElement, (e) =>
          datasetMap.has(e.id),
        )({ iElements });
        if (ancestor) {
          return ancestor;
        }

        const closestPick = pickClosestInTime(referenceElement, elements);
        if (closestPick && isIElementImg2d(closestPick)) {
          return findSelectedPhotosGroup(datasets, closestPick);
        }
        return closestPick;
      }
    },
  ),
);
